import React, { useRef, useState } from 'react';
import { DRAWER_Z_INDEX } from '../../../../definitions/constants';
import {
  OwnerAssignmentProps,
  OwnerInvitationProps,
  OwnerInvitedCallback,
  ShareRoomProps,
} from './types';
import { CollaboratorType } from '../../../../enums/CollaboratorType';
import { ContactSchema } from '../../../../definitions/schemas';
import { stopEffects } from '../../../../lib/Event';
import ShareRoomDialog from '../../../Rooms/Share/ShareRoomDialog';

export interface WithOwnerInvitationProps {
  ShareRoomDialogProps?: ShareRoomProps;
}

type WrappedComponentProps<P> = P & OwnerInvitationProps;

/**
 * Higher order component to wrap the OwnerSelection and OwnerAutocomplete
 * components, to grant them the ability to open up the share room dialog
 * and allow users to invite contacts to the room and automatically assign
 * them as task owners. The order of execution is something like this:
 *  1. The user searches by name or email a contact
 *  2. The contact is not found, so an option to send an invitation is surfaced
 *  3. The user selects that option, and the share room dialog is opened
 *    3.1. A reference to the success callback is stored, in order to be
 *         executed later when the invited contacts are successfully created
 *  4. The user enters the name of one or more contacts and sends the invitations
 *    4.1. The onContactsInvited callback is executed, which allows the root
 *         component to refresh the available contact options in order to show
 *         the recently created contacts as available options
 *    4.2. The success callback is executed, in order for the wrapped component
 *         to update their state and add those contacts as the task owners
 */
function withOwnerInvitations<Props extends OwnerAssignmentProps>(
  WrappedComponent: React.FC<WrappedComponentProps<Props>>
) {
  const ComponentWithOwnerInvite: React.FC<
    WrappedComponentProps<Props> & WithOwnerInvitationProps
  > = (
    props: WrappedComponentProps<Props> & WithOwnerInvitationProps
  ): JSX.Element => {
    const { ShareRoomDialogProps, ...wrappedComponentProps } = props;

    const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
    const [emails, setEmails] = useState<Array<string>>();

    // using a ref since you can't store functions in useState
    const ownersInvitedCbRef = useRef<OwnerInvitedCallback>();

    const handleClose = () => {
      setInvitationDialogOpen(false);
      setEmails(undefined);

      ownersInvitedCbRef.current = undefined;
    };

    /**
     * Wraps the ShareRoomDialogProps.onContactsInvited with a function
     * that notifies the root component that a new collaborator was added and
     * then executes the callback when an owner is invited successfully,
     * in order to grant access to the wrapped component to the
     * recently created records.
     */
    const handleContactsInvited = (contacts: ContactSchema[]) => {
      if (!ShareRoomDialogProps) {
        ownersInvitedCbRef.current = undefined;
        return;
      }

      const { onContactsInvited } = ShareRoomDialogProps;

      if (onContactsInvited) {
        onContactsInvited(contacts);
      }

      if (contacts && ownersInvitedCbRef.current) {
        const newOwners = contacts.map((contact) => ({
          ownerId: contact.id,
          ownerType: CollaboratorType.Contact,
        }));

        ownersInvitedCbRef.current(newOwners);
      }

      ownersInvitedCbRef.current = undefined;
    };

    /**
     * Handler passed to the wrapped component to open the share room
     * dialog. This stores a reference to a callback that is executed
     * when an owner is invited successfully. The reference is maintained
     * because it's given as a parameter to the dialog and executed when
     * the user finishes the invitation flow.
     */
    const onOwnerInvite = (
      email: string,
      onOwnersInvited: OwnerInvitedCallback
    ) => {
      setEmails([email]);
      setInvitationDialogOpen(true);
      ownersInvitedCbRef.current = onOwnersInvited;
    };

    return (
      <>
        <WrappedComponent
          {...(wrappedComponentProps as WrappedComponentProps<Props>)}
          onInviteOwner={onOwnerInvite}
        />
        {ShareRoomDialogProps && (
          <ShareRoomDialog
            {...ShareRoomDialogProps}
            actionLabel="Invite"
            DialogProps={{
              disableEscapeKeyDown: true,
              onClick: stopEffects(),
              style: { zIndex: DRAWER_Z_INDEX + 1 },
            }}
            disableDescription
            emails={emails}
            forceEmailInput
            onClose={handleClose}
            onContactsInvited={handleContactsInvited}
            open={invitationDialogOpen}
            title="Invite to room, and assign as task owner"
          />
        )}
      </>
    );
  };

  return ComponentWithOwnerInvite;
}

export default withOwnerInvitations;
